// React Query hooks for the WB contracts admin surface (Phase C4 Group 2).
//
// Pairs with /api/v1/wb/contracts/* — list (non-paginated, optionally filtered
// by customer_enc_id / status) / detail / create / patch / delete + line
// bulk-replace via PUT /:enc_id/lines.
//
// DTOs mirror server/src/modules/wb/contracts/types.ts (see
// WEIGHBRIDGE_SERVER_CONTRACT.md §C4 Contract). `enc_id` is the
// encrypted scalar id (numeric customer_id / contract_id encrypted via
// `encryptField(String(row.id))` on the server).

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '../../../api/client';
import { endpoints } from '../../../api/endpoints';
import { queryKeys } from '../../../api/queryKeys';

// -- Client-side types (mirror WbContractDto on the server) ---------------

export type ContractStatus = 'draft' | 'active' | 'expired' | 'cancelled';

export type ContractLineType =
  | 'manual'
  | 'plus_amount'
  | 'minus_amount'
  | 'plus_percent'
  | 'minus_percent';

export interface WbContractCustomerSummary {
  readonly enc_id: string;
  readonly name: string;
}

export interface WbContractLineProduct {
  readonly enc_id: string;
  readonly name: string;
  readonly code: string;
}

export interface WbContractLine {
  readonly product: WbContractLineProduct;
  readonly type: ContractLineType;
  readonly value: number;
  /** Server-rendered (e.g. "£12.50/tonne" or "+5%"). Read-only on the
   *  client — input PUTs must NEVER include this field. */
  readonly label: string;
}

export interface WbContractListItem {
  readonly enc_id: string;
  readonly customer: WbContractCustomerSummary;
  readonly name: string;
  readonly valid_from: string;
  readonly valid_to: string;
  readonly status: ContractStatus;
  readonly line_count: number;
  readonly created_at: string;
  readonly updated_at: string;
}

export interface WbContractDetail extends WbContractListItem {
  readonly notes: string | null;
  readonly lines: WbContractLine[];
}

// -- Input shapes ----------------------------------------------------------

export interface CreateContractInput {
  customer_enc_id: string;
  name: string;
  valid_from: string;
  valid_to: string;
  status?: ContractStatus;
  notes?: string | null;
}

/** PATCH /wb/contracts/:enc_id — `customer_enc_id` is immutable post-create
 *  and is INTENTIONALLY omitted here. The server returns 422 if you send it. */
export interface UpdateContractInput {
  name?: string;
  valid_from?: string;
  valid_to?: string;
  status?: ContractStatus;
  notes?: string | null;
}

export interface ContractLineInput {
  product_enc_id: string;
  type: ContractLineType;
  value: number;
}

// -- List filters ----------------------------------------------------------

export interface ListContractsParams {
  customer_enc_id?: string;
  status?: ContractStatus;
}

interface ListContractsResponse {
  contracts: WbContractListItem[];
}

interface ContractListLinesResponse {
  lines: WbContractLine[];
}

// -- Queries ---------------------------------------------------------------

export function useWbContracts(params: ListContractsParams = {}) {
  return useQuery<ListContractsResponse>({
    queryKey: queryKeys.wb.contracts(params as Record<string, unknown>),
    queryFn: async () => {
      const res = await apiClient.get<ListContractsResponse>(
        endpoints.wb.contracts,
        { params },
      );
      return res.data;
    },
    staleTime: 30_000,
  });
}

export function useWbContract(encId: string | undefined) {
  const enabled = !!encId && encId !== 'new';
  return useQuery<{ contract: WbContractDetail }>({
    queryKey: queryKeys.wb.contract(encId ?? ''),
    queryFn: async () => {
      if (!encId) throw new Error('useWbContract called without enc_id');
      const res = await apiClient.get<{ contract: WbContractDetail }>(
        endpoints.wb.contract(encId),
      );
      return res.data;
    },
    enabled,
    staleTime: 30_000,
  });
}

export function useWbContractLines(encId: string | undefined) {
  const enabled = !!encId && encId !== 'new';
  return useQuery<ContractListLinesResponse>({
    queryKey: queryKeys.wb.contractLines(encId ?? ''),
    queryFn: async () => {
      if (!encId) throw new Error('useWbContractLines called without enc_id');
      const res = await apiClient.get<ContractListLinesResponse>(
        endpoints.wb.contractLines(encId),
      );
      return res.data;
    },
    enabled,
    staleTime: 30_000,
  });
}

// -- Mutations -------------------------------------------------------------

export function useCreateContract() {
  const qc = useQueryClient();
  return useMutation<
    { contract: WbContractDetail },
    Error,
    CreateContractInput
  >({
    mutationFn: async (body) => {
      const res = await apiClient.post<
        { contract: WbContractDetail },
        CreateContractInput
      >(endpoints.wb.contracts, body);
      return res.data;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.wb.contracts() });
    },
  });
}

export function useUpdateContract(encId: string) {
  const qc = useQueryClient();
  return useMutation<
    { contract: WbContractDetail },
    Error,
    UpdateContractInput
  >({
    mutationFn: async (body) => {
      // Defensive: strip customer_enc_id if a caller ever accidentally
      // includes it (the server returns 422 anyway).
      const safeBody = { ...body } as UpdateContractInput & {
        customer_enc_id?: unknown;
      };
      delete safeBody.customer_enc_id;
      const res = await apiClient.patch<
        { contract: WbContractDetail },
        UpdateContractInput
      >(endpoints.wb.contract(encId), safeBody);
      return res.data;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.wb.contracts() });
      void qc.invalidateQueries({ queryKey: queryKeys.wb.contract(encId) });
    },
  });
}

export function useDeleteContract(encId: string) {
  const qc = useQueryClient();
  return useMutation<{ deleted: true }, Error, void>({
    mutationFn: async () => {
      const res = await apiClient.delete<{ deleted: true }>(
        endpoints.wb.contract(encId),
      );
      return res.data;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.wb.contracts() });
      void qc.invalidateQueries({ queryKey: queryKeys.wb.contract(encId) });
    },
  });
}

export function useReplaceContractLines(encId: string) {
  const qc = useQueryClient();
  return useMutation<
    ContractListLinesResponse,
    Error,
    { lines: ContractLineInput[] }
  >({
    mutationFn: async (body) => {
      const res = await apiClient.put<
        ContractListLinesResponse,
        { lines: ContractLineInput[] }
      >(endpoints.wb.contractLines(encId), body);
      return res.data;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.wb.contract(encId) });
      void qc.invalidateQueries({
        queryKey: queryKeys.wb.contractLines(encId),
      });
    },
  });
}
